import { FC, useState } from 'react';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { TCertificate, TJoint } from '../../../lib/types';
import { useMutation, useQuery } from '@apollo/client';
import { Table } from '@bphxd/ds-core-react';
import { SIGN_JOINT_STEP } from '../../../graph-queries/tar/joint';
import { GET_CERTIFICATE_DETAILS } from '../../../graph-queries/tar/certificate';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import classes from './BulkSigningModal.module.scss';
import { CertificateTypeEnum } from '../../../graph-queries/__generated__/graphql';
import { jointStatus } from '../JointDetails/JointDetails';

type JointStatus = {
  isReadyToBeSigned: boolean;
  isAlreadySigned: boolean;
  message: string;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  stepNo: number;
  stepTitle: string;
  certificateId: number;
  onComplete: () => void;
  selectedJoints: TJoint[];
};

const BulkSigningModal: FC<Props> = ({
  isOpen,
  onClose,
  stepNo,
  stepTitle,
  certificateId,
  onComplete,
  selectedJoints
}) => {
  const [comments, setComments] = useState('');
  const [isSigning, setIsSigning] = useState(false);

  // Get certificate details to access joints
  const { data: certificateDetails } = useQuery(GET_CERTIFICATE_DETAILS, {
    variables: { certificateId },
  });
  const certificate = certificateDetails?.certificateDetails as unknown as TCertificate;

  const [signJointStep] = useMutation(SIGN_JOINT_STEP, {
    refetchQueries: [
      { query: GET_CERTIFICATE_DETAILS, variables: { certificateId } },
    ],
    awaitRefetchQueries: true,
  });

  const { sendSuccess, sendError } = useNoticeBar();

  const readyForNextPhase = (): {
    readyForLeakTest: boolean;
    readyForStartup: boolean;
  } => {
    return jointStatus(certificate);
  };

  const handleSignAll = async () => {
    if (selectedJoints.length === 0) return;
    let actualJointsSignedCount = 0;
    try {
      // Sign the steps
      const signPromises = selectedJoints.map((joint) => {
        const step = joint.tarJointInstallationSteps?.find((s) => s.stepNo === stepNo);
        if (!step) {
          throw new Error(`Step ${stepNo} ${stepTitle} not found for joint ${joint.jointNumber}`);
        }

        if (step.signedBy) {
          // Skip already signed steps
          return Promise.resolve();
        }

        actualJointsSignedCount++;

        return signJointStep({
          variables: {
            stepId: step.stepId,
            comments: comments || undefined,
          },
        });
      });

      await Promise.all(signPromises);

      sendSuccess(`✅ "${stepTitle}" signed for ${actualJointsSignedCount} joint(s)`);
      onComplete();
      onClose();
      setComments('');
    } catch (error) {
      console.error('Bulk joint signing error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      sendError('Failed to bulk sign joints');
    } finally {
      setIsSigning(false);
    }
  };

  const getJointStatus = (joint: TJoint): JointStatus => {
    const currentStep = joint.tarJointInstallationSteps?.find((s) => s.stepNo === stepNo);

    if (currentStep === undefined) {
      return {
        isReadyToBeSigned: false,
        isAlreadySigned: false,
        message: ` ${stepTitle} step not found for joint`,
      };
    }

    // Check if already signed
    if (currentStep?.signedBy) {
      return {
        isReadyToBeSigned: false,
        isAlreadySigned: true,
        message: `Already signed on ${new Date(currentStep.signedAt).toLocaleDateString()}`,
      };
    }

    // CHECK IF JOINT READY TO BE SIGNED FOR CURRENT STEP
    const { readyForLeakTest, readyForStartup } = readyForNextPhase();

    // if current step is Leak Test (stepNo 5), check if all installation steps are signed
    if (currentStep && currentStep.stepNo === 5 && !readyForLeakTest) {
      return {
        isReadyToBeSigned: false,
        isAlreadySigned: false,
        message: 'Installation steps for 1 or more joints not completed',
      };
    }

    // if current step is Operational phase (stepNo >=7), check if Leak Test is signed
    if (currentStep && currentStep.stepNo >= 6 && !readyForStartup) {
      return {
        isReadyToBeSigned: false,
        isAlreadySigned: false,
        message: 'Previous phases not completed for 1 or more joints',
      };
    }

    return {
      isReadyToBeSigned: true,
      isAlreadySigned: false,
      message: 'Ready to sign',
    };
  };

  const hasStepNotFoundJoints = selectedJoints.some((joint) => {
    const step = joint.tarJointInstallationSteps?.find((s) => s.stepNo === stepNo);
    return step === undefined;
  });

  const readyToBeSignedJoints = selectedJoints.filter((joint) => getJointStatus(joint).isReadyToBeSigned);
  const areAllJointsAlreadySigned = selectedJoints.length - readyToBeSignedJoints.length === selectedJoints.length;

  // Check if all joints are either ready to be signed OR already signed
  const allReadyOrSigned = selectedJoints.every((joint) => {
    const status = getJointStatus(joint);
    return status.isReadyToBeSigned || status.isAlreadySigned;
  });

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        Sign &quot;{stepTitle}&quot; for {selectedJoints.length} joint(s)
      </ModalHeader>
      <ModalBody>
        <div className={classes.jointsList}>
          <h6>You are about to sign the following</h6>
          <Table>
            <Table.Head>
              <Table.Header>Joint Number</Table.Header>
              <Table.Header>TAG ID</Table.Header>
              <Table.Header>Status</Table.Header>
            </Table.Head>
            <Table.Body>
              {selectedJoints.map((joint, index) => {
                const status = getJointStatus(joint);
                return (
                  <Table.Row key={joint.jointId} rowIndex={index}>
                    <Table.Cell>{joint.jointNumber}</Table.Cell>
                    <Table.Cell>{joint.tagId}</Table.Cell>
                    <Table.Cell>
                      <span className={(status.isReadyToBeSigned || status.isAlreadySigned) ? `text-success`: `text-danger`}>
                        {status.message}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>

          {allReadyOrSigned && readyToBeSignedJoints.length < selectedJoints.length && (
            <div className="alert alert-info mt-3">
              {selectedJoints.length - readyToBeSignedJoints.length} joint(s) already signed. Only{' '}
              {readyToBeSignedJoints.length} joint(s) will be signed.
            </div>
          )}
          {!allReadyOrSigned && hasStepNotFoundJoints && (
            <div className="alert alert-danger mt-3">
              Step &quot;{stepTitle}&quot; not found for one or more joints. Cannot proceed with signing.
            </div>
          )}
          {!allReadyOrSigned && !hasStepNotFoundJoints && (
            <div className="alert alert-warning mt-3">
              Some joints cannot be signed due to incomplete previous phases. Please complete required steps first.
            </div>
          )}
        </div>

        <FormGroup className="mt-4">
          <Label for="bulk-comments">Comments (optional, applies to all):</Label>
          <Input
            type="textarea"
            id="bulk-comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Enter comments for this step..."
            rows={3}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSigning}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSignAll}
          disabled={isSigning || !allReadyOrSigned || areAllJointsAlreadySigned}
        >
          Sign {readyToBeSignedJoints.length > 0 ? `${readyToBeSignedJoints.length}` : 'All'} Joint(s)
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkSigningModal;
